import {
  Body,
  Box,
  Button,
  CheckboxField,
  Dialog,
  ErrorBanner,
  FormRow,
  FormSection,
  Heading,
  buttonLinkG4P,
  Stack,
  unsafeLocalizedString,
  useForm,
  useIsMobileLayout,
  useUniqueID,
  validators,
  Space,
  useLoadingStatusRead,
} from "@dbn/omnichannel-design-system-g4p";
import { boolean, option, taskEither } from "fp-ts";
import {
  constFalse,
  constNull,
  constVoid,
  identity,
  pipe,
} from "fp-ts/function";
import {
  foldTenant,
  MoneyAmount,
  unsafeNonNegativeInteger,
} from "../../../globalDomain";
import {
  RuntimeLocaleKey,
  useFormatMessage,
  useFormatMoneyAmount,
  useFormatRichMessage2,
} from "../../../intl";
import * as api from "../api";
import { CoverageCard } from "./CoverageCard";
import { useAppContext } from "../../../useAppContext";
import { IO } from "fp-ts/IO";
import { Option } from "fp-ts/Option";
import * as classes from "./InsuranceChoose.css";
import cx from "classnames";
import { useCommand } from "../../../useAPI";
import { useEffect, useRef, useState } from "react";
import { FilePDFDialog } from "../../../Common/Dialogs/FilePDFDialog/FilePDFDialog";
import { YesNoRadioGroupField } from "../../../Common/YesNoRadioGroup/YesNoRadioGroupField";
import { TaskEither } from "fp-ts/TaskEither";
import { palette } from "@dbn/omnichannel-design-system-g4p/lib/styleConstants";
import { InfoTooltip } from "../../../Common/InfoTooltip/InfoTooltip";
import { Spinner } from "@g4p/react-styleguide-library";

export interface CPIAdditionalQuestions {
  consideringFinancial: boolean;
  declared: boolean;
  experienceWithCPI: Option<boolean>;
  knowledge: Option<boolean>;
  options: boolean;
}

type Props = {
  onBack: (additionalAnswers: Option<CPIAdditionalQuestions>) => unknown;
  onInsuranceChosen: (
    chosenInsuranceType: api.CreditProtectionInsuranceType,
    additionalAnswers: Option<CPIAdditionalQuestions>
  ) => TaskEither<unknown, unknown>;
  onDismiss: (
    additionalAnswers: Option<CPIAdditionalQuestions>
  ) => TaskEither<unknown, unknown>;
  didAnswerSolvencyQuestions: boolean;
  cpiPackageList: api.CPIPackageList;
  currentInsuranceType: api.CreditProtectionInsuranceType;
  currentOfferInstallment: MoneyAmount;
  onReviewSolvencyQuestions: IO<unknown>;
  additionalAnswers: Option<CPIAdditionalQuestions>;
  solvencyAnswered: boolean;
  noSelection: boolean;
  onNoCPIWanted: () => unknown;
};

export function InsuranceChoose(props: Props) {
  const formatMessage = useFormatMessage();
  const formatRichMessage = useFormatRichMessage2();
  const isMobileLayout = useIsMobileLayout();
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const insuranceDocument = useCommand(api.insuranceDocument);
  const setAdditionalQuestions = useCommand(api.setAdditionalQuestions);
  const [hasFormError, setHasFormError] = useState(false);
  const dialogContainerRef = useRef<HTMLElement>(null);
  const uniqueId = useUniqueID();
  const { isSubmitting } = useLoadingStatusRead();

  const currentAdditionalAnswers = useRef<Option<CPIAdditionalQuestions>>(
    option.none
  );

  const initialValues = pipe(
    props.additionalAnswers,
    option.fold(
      () => ({
        options: false,
        consideringFinancial: option.none,
        declared: false,
        experienceWithCPI: option.none,
        knowledge: option.none,
      }),
      additionalAnswers => ({
        ...additionalAnswers,
        consideringFinancial: option.some(
          additionalAnswers.consideringFinancial
        ),
      })
    )
  );

  const wipAdditionalAnswers = useRef<CPIAdditionalQuestions>({
    ...initialValues,
    consideringFinancial: pipe(
      initialValues.consideringFinancial,
      option.getOrElse(constFalse)
    ),
  });

  const {
    apiParameters: { tenant },
  } = useAppContext();

  useEffect(() => {
    setTimeout(() => {
      if (isMobileLayout && !!dialogContainerRef.current) {
        (dialogContainerRef.current
          .parentElement as HTMLDivElement).style.height = "";
      }
    }, 0);
  }, []);

  const { fieldProps, handleSubmit, values } = useForm(
    {
      initialValues,
      fieldValidators: () =>
        foldTenant(
          tenant,
          () => ({
            declared: validators.checked(
              formatMessage(
                "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.skCheckError"
              )
            ),
            consideringFinancial: validators.inSequence(
              validators.defined<boolean>(
                formatMessage(
                  "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.skConsideringFinancialError"
                )
              ),
              validators.checked(
                formatMessage(
                  "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.skConsideringFinancialError"
                )
              )
            ),
            options: validators.checked(
              formatMessage(
                "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.skCheckError"
              )
            ),
            experienceWithCPI: validators.defined<boolean>(
              formatMessage(
                "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.experienceWithCPIError"
              )
            ),
            knowledge: validators.defined<boolean>(
              formatMessage(
                "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.knowledgeError"
              )
            ),
          }),
          () => ({})
        ),
    },
    {
      onSubmit: data =>
        foldTenant(
          tenant,
          () =>
            pipe(
              setAdditionalQuestions({
                declared: data.declared,
                // This is validated when tenant is "SK", so it's a boolean
                consideringFinancial: (data.consideringFinancial as unknown) as boolean,
                experienceWithCPI: (data.experienceWithCPI as unknown) as boolean,
                knowledge: (data.knowledge as unknown) as boolean,
              }),
              taskEither.chain(() =>
                taskEither.fromIO(() => {
                  currentAdditionalAnswers.current = option.some({
                    declared: data.declared,
                    consideringFinancial: (data.consideringFinancial as unknown) as boolean,
                    experienceWithCPI: option.some(
                      (data.experienceWithCPI as unknown) as boolean
                    ),
                    knowledge: option.some(
                      (data.knowledge as unknown) as boolean
                    ),
                    options: (data.options as unknown) as boolean,
                  });
                })
              )
            ),
          () => taskEither.fromIO(constVoid)
        ),
    }
  );

  useEffect(() => {
    wipAdditionalAnswers.current = {
      ...values,
      consideringFinancial: pipe(
        values.consideringFinancial,
        option.getOrElse(constFalse)
      ),
    };
  }, [values]);

  const formatMoneyAmount = useFormatMoneyAmount(
    unsafeNonNegativeInteger(
      foldTenant(
        tenant,
        () => 2,
        () => 0
      )
    )
  );

  function renderCard(insurance: api.CreditProtectionInsurance) {
    const isSelected = insurance.type === props.currentInsuranceType;

    const onSelectInsurance = pipe(
      taskEither.fromIO(() => setHasFormError(false)),
      taskEither.chain(() => handleSubmit),
      taskEither.fold(
        () => taskEither.fromIO(() => setHasFormError(true)),
        () =>
          props.onInsuranceChosen(
            insurance.type,
            currentAdditionalAnswers.current
          )
      )
    );

    const onKeepInsurance = pipe(
      taskEither.fromIO(() => setHasFormError(false)),
      taskEither.chain(() => handleSubmit),
      taskEither.fold(
        () => taskEither.fromIO(() => setHasFormError(true)),
        () =>
          taskEither.fromIO(() =>
            props.onBack(option.some(wipAdditionalAnswers.current))
          )
      )
    );

    return (
      <CoverageCard
        key={insurance.name}
        creditProtectionInsurance={insurance}
        onSelectInsurance={onSelectInsurance}
        onKeepInsurance={
          props.solvencyAnswered ? onSelectInsurance : onKeepInsurance
        }
        selected={isSelected}
        installment={props.currentOfferInstallment}
        disabled={!insurance.eligible}
        isSuggested={false}
        isExpanded={props.solvencyAnswered}
      />
    );
  }

  useEffect(() => {
    pipe(
      document.getElementById("errorBanner"),
      option.fromNullable,
      option.map(e => {
        if (hasFormError) {
          e.scrollIntoView({ block: "start", behavior: "smooth" });
        }
      })
    );
  }, [hasFormError]);

  useEffect(() => {
    if (!showMoreInfo) {
      foldTenant(
        tenant,
        () => {
          const buttonParent = document.getElementById(uniqueId);
          const button = buttonParent?.querySelector(
            ".inlineLink"
          ) as HTMLElement;
          button?.focus();
          button?.blur();
        },
        () => {
          const button = document.getElementById(uniqueId);
          button?.focus();
          button?.blur();
        }
      );
    }
  }, [showMoreInfo]);

  const onDismiss =
    props.currentInsuranceType === props.cpiPackageList.selectedInsurance
      ? option.some(() =>
          props.onBack(option.some(wipAdditionalAnswers.current))
        )
      : option.some(
          props.onInsuranceChosen(
            props.currentInsuranceType,
            currentAdditionalAnswers.current
          )
        );

  const onCancelInsurance = () => {
    if (props.noSelection) {
      props.onNoCPIWanted();
    }
    props.onInsuranceChosen(
      "None",
      option.some(wipAdditionalAnswers.current)
    )();
  };

  return (
    <Dialog
      ariaLabelMessageForCloseIcon={formatMessage(
        "Modal.closeButton" as RuntimeLocaleKey
      )}
      variant="left"
      title={unsafeLocalizedString("")}
      actions={[]}
      size="large"
      onDismiss={onDismiss}
      mobileModalHeight={"90dvh"}
    >
      <Stack
        column
        shrink
        units={0}
        style={{ margin: "0 6px" }}
        ref={dialogContainerRef}
      >
        <Spinner show={isSubmitting} />
        <Stack shrink column units={3}>
          {props.noSelection && (
            <Heading size="large" weight="medium" align="left">
              {formatMessage(
                "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.titleHeader"
              )}
            </Heading>
          )}
          <Heading size="small" weight="medium" align="left">
            {formatMessage(
              "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.title"
            )}
          </Heading>

          <Body size="medium" weight="regular">
            {formatMessage(
              "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.paragraph1"
            )}
          </Body>
          <Body size="medium" weight="regular">
            {formatMessage(
              "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.paragraph2",
              {
                monthlyInstallment: formatMessage(
                  "MoneyAmount",
                  formatMoneyAmount(props.currentOfferInstallment, identity)
                ),
              }
            )}
          </Body>
          <Box column hAlignContent="left">
            {pipe(
              foldTenant(
                tenant,
                () => (
                  <Stack column units={6} id={uniqueId}>
                    <CheckboxField
                      label={[
                        formatMessage(
                          "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.skCheck"
                        ),
                        buttonLinkG4P(
                          () => setShowMoreInfo(true),
                          formatMessage(
                            "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.moreInfoLink"
                          ),
                          formatMessage(
                            "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.moreInfoLink.ariaLabel"
                          )
                        ),
                      ]}
                      {...fieldProps("declared")}
                      onChange={checked => {
                        fieldProps("declared").onChange(checked);
                        if (checked) {
                          setShowMoreInfo(true);
                        }
                      }}
                    />

                    <FormSection>
                      <FormRow type="full">
                        <YesNoRadioGroupField
                          {...fieldProps("experienceWithCPI")}
                          label={formatMessage(
                            "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.skCPIExperience"
                          )}
                        />
                      </FormRow>
                      <FormRow type="full">
                        <YesNoRadioGroupField
                          {...fieldProps("knowledge")}
                          label={formatMessage(
                            "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.skKnowledge"
                          )}
                        />
                      </FormRow>
                    </FormSection>
                  </Stack>
                ),
                () => (
                  <Button
                    id={uniqueId}
                    variant="text"
                    size="default"
                    label={formatMessage(
                      "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.moreInfoLink"
                    )}
                    ariaLabel={formatMessage(
                      "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.moreInfoLink.ariaLabel"
                    )}
                    action={() => setShowMoreInfo(true)}
                  />
                )
              )
            )}
          </Box>
          {foldTenant(tenant, constNull, () => (
            <span style={{ position: "relative" }}>
              <Body size="medium" weight="regular" color={palette.dark}>
                {formatMessage(
                  "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.paragraph3.beforeLink"
                )}
                {buttonLinkG4P(
                  () => {
                    window.location.href = formatMessage(
                      "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.paragraph3.link.url"
                    ).toString();
                  },
                  formatMessage(
                    "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.paragraph3.link"
                  ),
                  formatMessage(
                    "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.paragraph3.ariaLabel"
                  )
                )}
                {formatMessage(
                  "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.paragraph3.afterLink"
                )}
              </Body>
              <InfoTooltip
                inline
                content={formatMessage(
                  "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.tooltip"
                )}
              />
            </span>
          ))}
          <Body size="medium" weight="regular">
            {formatRichMessage(
              "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.paragraph4"
            )}
          </Body>
        </Stack>
        <Space units={10} />
        <Stack column shrink units={10}>
          <Stack grow units={8} height="fit-content" column={isMobileLayout}>
            {props.cpiPackageList.options.map(option => renderCard(option))}
          </Stack>
          {props.didAnswerSolvencyQuestions && (
            <Box hAlignContent="left">
              <Button
                variant="link"
                action={props.onReviewSolvencyQuestions}
                label={formatMessage(
                  "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.reviewSolvency"
                )}
              />
            </Box>
          )}
          {pipe(
            hasFormError,
            boolean.fold(constNull, () => (
              <ErrorBanner
                id="errorBanner"
                children={formatMessage(
                  "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.requiredFormError"
                )}
              />
            ))
          )}
          {pipe(
            isMobileLayout,
            boolean.fold(
              () => (
                <Box hAlignContent="right">
                  <Button
                    variant="primary"
                    size="default"
                    action={onCancelInsurance}
                    label={formatMessage(
                      "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.cancelInsurance"
                    )}
                  />
                </Box>
              ),
              () => (
                <Box hAlignContent="center" column>
                  <Button
                    className={cx(
                      isMobileLayout && classes.cancelInsuranceButtonMobile
                    )}
                    variant="primary"
                    size="default"
                    action={onCancelInsurance}
                    label={formatMessage(
                      "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.cancelInsuranceMobile"
                    )}
                  />
                </Box>
              )
            )
          )}
          {showMoreInfo && (
            <FilePDFDialog
              onDismiss={() => {
                setShowMoreInfo(false);
                fieldProps("declared").onChange(true);
              }}
              title={formatMessage(
                "StandardLoan.CustomerOffer.Document.insuranceAgreement"
              )}
              file={insuranceDocument()}
            />
          )}
          {pipe(
            foldTenant(
              tenant,
              () => (
                <FormSection>
                  <FormRow type="full">
                    <YesNoRadioGroupField
                      {...fieldProps("consideringFinancial")}
                      label={formatMessage(
                        "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.skFinancialScope"
                      )}
                    />
                  </FormRow>
                  <FormRow type="full">
                    <CheckboxField
                      label={formatMessage(
                        "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.skOptionOne"
                      )}
                      {...fieldProps("options")}
                    />
                  </FormRow>
                  <FormRow type="full">
                    <CheckboxField
                      disabled
                      value={false}
                      name="optionTwo"
                      issues={option.none}
                      onBlur={constVoid}
                      onChange={constVoid}
                      label={formatMessage(
                        "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.skOptionTwo"
                      )}
                    />
                  </FormRow>
                  <FormRow type="full">
                    <CheckboxField
                      disabled
                      value={false}
                      name="optionThree"
                      issues={option.none}
                      onBlur={constVoid}
                      onChange={constVoid}
                      label={formatMessage(
                        "StandardLoan.CustomerOffer.CPIDialog.InsuranceChoose.skOptionThree"
                      )}
                    />
                  </FormRow>
                </FormSection>
              ),
              constNull
            )
          )}
        </Stack>
      </Stack>
    </Dialog>
  );
}
